import { Add, ArrowForwardIos } from '@mui/icons-material';
import { Button, Chip, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Page, PageTitle } from 'src/components';
import Question from 'src/components/exam/Question';
import { createStep, errorHandler, updateStep } from 'src/services';

export default function CreateExam({ onCancel, onCreate, initialData }) {
  const courseID = useSelector((state) => state.course.courseID);
  const [title, setTitle] = useState(initialData?.title);
  const [questions, setQuestions] = useState(
    initialData?.questions || [
      {
        title: '',
        point: 0,
        options: ['', ''],
        answer: '',
      },
    ]
  );
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [error, setError] = useState(false);
  const [points, setPoints] = useState({
    reward: initialData?.score || 0,
    required: initialData?.passScore || 0,
  });
  const [loading, setLoading] = useState(false);
  const checkEmptyOptions = () => {
    return questions.map(({ options }) => options.some((option) => option === '')).includes(true);
  };
  const checkNotAnswered = () => {
    return questions.map(({ answer }) => answer === '').includes(true);
  };
  const appendQuestion = () => {
    setActiveQuestion(questions.length);
    const newQuestion = {
      title: '',
      point: 0,
      options: ['', ''],
      answer: '',
    };
    setQuestions([...questions, newQuestion]);
  };
  const isSubmitDisabled = useMemo(
    () => checkEmptyOptions() || checkNotAnswered() || !title.trim(),
    [questions, title]
  );
  const onSubmit = async () => {
    if (points.required === 0 || points.reward === 0) {
      setError(true);
      return;
    } else {
      setLoading(true);
      try {
        if (initialData.id) {
          await updateStep(
            {
              courseID,
              properties: {
                title: title,
                type: {
                  value: 4,
                },
              },
              points: points,
              examLength: questions.length,
              exam: questions,
            },
            initialData.id
          );
        } else {
          await createStep({
            courseID,
            properties: {
              title: title,
              type: {
                value: 4,
              },
            },
            points: points,
            examLength: questions.length,
            exam: questions,
          });
        }
        setLoading(false);
        onCreate();
      } catch (error) {
        errorHandler(error?.response?.data.message);
        enqueueSnackbar('خطا در ایجاد آزمون', { variant: 'error' });
        setLoading(false);
      }
    }
  };
  return (
    <Page title="ایجاد آزمون">
      <PageTitle title="ایجاد آزمون" />
      <Stack flexDirection={'column'} gap={4}>
        <TextField label="عنوان" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} />
        {questions.length === 0 ? (
          <Typography variant="caption" sx={{ display: 'block', color: '#FF4842' }}>
            لطفا سوالات خود را اضافه کنید *
          </Typography>
        ) : null}
        {questions.map((question, index) => (
          <Question
            key={index}
            index={index + 1}
            title={question.title}
            isActive={activeQuestion === index}
            options={question.options}
            answer={question.answer}
            onEdit={() => setActiveQuestion(index)}
            onDelete={() => setQuestions(questions.filter((_, qIndex) => qIndex !== index))}
            onChange={(modifiedQuestion) => {
              const updatedQuestions = [...questions];
              updatedQuestions[index] = modifiedQuestion;
              setQuestions(updatedQuestions);
            }}
          />
        ))}
        <Divider sx={{ my: 3 }}>
          {checkNotAnswered() || checkEmptyOptions() ? (
            checkNotAnswered() ? (
              <Chip label="لطفا پاسخ سوال را مشخص کنید" variant="outlined" />
            ) : (
              <Chip label="لطفا همه گزینه های خالی را پر کنید" variant="outlined" />
            )
          ) : (
            <Button onClick={appendQuestion} endIcon={<Add />} variant="contained">
              افزودن سوال
            </Button>
          )}
        </Divider>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TextField
              label="امتیاز آزمون"
              value={points.reward}
              onChange={(e) => {
                setPoints((p) => {
                  if (!!e.target.valueAsNumber) {
                    return { ...p, reward: e.target.valueAsNumber };
                  } else {
                    return { ...p, reward: 0 };
                  }
                });
              }}
              fullWidth
              type="number"
              error={error && points.reward === 0}
              inputProps={{ min: 0 }}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="حد نصاب امتیاز"
              value={points.required}
              onChange={(e) => {
                setPoints((p) => {
                  if (!!e.target.valueAsNumber) {
                    if (p.reward >= e.target.valueAsNumber) {
                      return { ...p, required: e.target.valueAsNumber };
                    } else {
                      return { ...p, required: p.required };
                    }
                  } else {
                    return { ...p, required: 0 };
                  }
                });
              }}
              fullWidth
              type="number"
              error={error && (points.required === 0 || points.required > points.reward)}
              inputProps={{ max: points.reward, min: 0 }}
              autoComplete="off"
              disabled={points.reward === 0}
            />
          </Grid>
        </Grid>
        <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Button
            startIcon={<ArrowForwardIos />}
            variant="outlined"
            disabled={loading}
            onClick={() => {
              onCancel();
            }}
          >
            بازگشت
          </Button>
          <Button
            variant="contained"
            disabled={isSubmitDisabled || loading}
            onClick={() => {
              onSubmit();
            }}
          >
            ثبت
          </Button>
        </Stack>
      </Stack>
    </Page>
  );
}
